import React from 'react';

import { useHasMapSync } from 'src/core/modules/accounting-integration/hooks/useHasMapSync';
import { rejectUnexpectedValue } from 'src/core/utils/switchGuard';

import { PullAndMapWithValuesSection } from './PullAndMapWithValuesSection';
import { StaticMapAndPullAndMapSection } from './StaticMapAndPullAndMapSection';
import { useUpdateAnalyticalFieldMapping } from '../../../../../../../accounting-integration/apis/useUpdateAnalyticalFieldMapping';
import {
  type AccountingSoftware,
  type AnalyticalFieldsCapability,
} from '../../../../../../integration/status';
import {
  type AnalyticalFieldMappingCreate,
  type AnalyticalFieldMappingDelete,
  useCreateAnalyticalFieldMappingMutation,
  useDeleteAnalyticalFieldMappingMutation,
} from '../../../../hooks/useCreateAnalyticalFieldMappingMutation';
import {
  type AnalyticalFieldMappingUpdate,
  useUpdateAnalyticalFieldMappingMutation,
} from '../../../../hooks/useUpdateAnalyticalFieldMappingMutation';

interface Props {
  integration: AccountingSoftware;
  analyticalFieldsCapability: AnalyticalFieldsCapability;
}

export const AnalyticalFieldsSection = ({
  integration,
  analyticalFieldsCapability,
}: Props) => {
  const [updateAnalyticalFieldMapping] =
    useUpdateAnalyticalFieldMappingMutation();
  const [deleteAnalyticalFieldMapping] =
    useDeleteAnalyticalFieldMappingMutation();
  const [createAnalyticalFieldMapping] =
    useCreateAnalyticalFieldMappingMutation();

  const hasMapSyncFeature = useHasMapSync();
  const { updateAnalyticalFieldMapping: updateAnalyticalFieldMappingV2 } =
    useUpdateAnalyticalFieldMapping();

  async function handleCreateAnalyticalFieldMapping(
    createdAnalyticalField: AnalyticalFieldMappingCreate,
  ) {
    if (createdAnalyticalField.codeType === 'costCenter') {
      await updateAnalyticalFieldMapping([
        {
          kind: 'mapped',
          codeType: 'costCenter',
          externalId: createdAnalyticalField.externalId,
          fieldId: createdAnalyticalField.fieldId,
        },
      ]);
      return;
    }

    await createAnalyticalFieldMapping([
      {
        kind: 'mapped',
        codeType: 'customField',
        externalId: createdAnalyticalField.externalId,
        externalName: createdAnalyticalField.externalName,
        internalName: createdAnalyticalField.internalName,
        fieldId: createdAnalyticalField.fieldId,
      },
    ]);
  }

  async function handleDeleteAnalyticalFieldMapping(
    deletedAnalyticalField: AnalyticalFieldMappingDelete & {
      externalName: string;
    },
  ) {
    const deletion: AnalyticalFieldMappingDelete = {
      kind: 'notMapped',
      externalId: deletedAnalyticalField.externalId,
      fieldId: deletedAnalyticalField.fieldId,
    };
    await deleteAnalyticalFieldMapping([deletion]);
  }

  async function handleUpdateAnalyticalFieldMapping(
    editedAnalyticalField: AnalyticalFieldMappingUpdate & {
      externalName: string;
    },
  ) {
    if (hasMapSyncFeature && editedAnalyticalField.fieldId) {
      const { kind, fieldId } = editedAnalyticalField;

      const getInternalId = () => {
        switch (kind) {
          case 'mapped':
            return editedAnalyticalField.codeType === 'costCenter'
              ? 'costCenter'
              : editedAnalyticalField.internalId;
          default:
            return null;
        }
      };

      const getFieldType = () => {
        switch (kind) {
          case 'mapped':
            return editedAnalyticalField.codeType === 'costCenter'
              ? ('costCenter' as const)
              : ('customField' as const);
          default:
            return null;
        }
      };

      updateAnalyticalFieldMappingV2({
        fieldId,
        fieldType: getFieldType(),
        internalId: getInternalId(),
      });

      return;
    }

    if (editedAnalyticalField.kind === 'notMapped') {
      await updateAnalyticalFieldMapping([
        {
          kind: 'notMapped',
          externalId: editedAnalyticalField.externalId,
          fieldId: editedAnalyticalField.fieldId,
        },
      ]);
    } else if (editedAnalyticalField.kind === 'mapped') {
      const update: AnalyticalFieldMappingUpdate =
        editedAnalyticalField.codeType === 'costCenter'
          ? {
              kind: 'mapped',
              codeType: 'costCenter',
              externalId: editedAnalyticalField.externalId,
              sendOnLine: editedAnalyticalField.sendOnLine,
              sendOnHeader: editedAnalyticalField.sendOnHeader,
              fieldId: editedAnalyticalField.fieldId,
            }
          : {
              kind: 'mapped',
              codeType: 'customField',
              externalId: editedAnalyticalField.externalId,
              internalName: editedAnalyticalField.internalName,
              internalId: editedAnalyticalField.internalId,
              sendOnLine: editedAnalyticalField.sendOnLine,
              sendOnHeader: editedAnalyticalField.sendOnHeader,
              fieldId: editedAnalyticalField.fieldId,
            };

      await updateAnalyticalFieldMapping([update]);
    }
  }

  switch (analyticalFieldsCapability) {
    case 'staticMap':
    case 'pullAndMapWithStaticFields':
    case 'pullAndMapWithStaticFieldsAndValues':
    case 'pullAndMap': {
      return (
        <StaticMapAndPullAndMapSection
          integration={integration}
          onUpdate={handleUpdateAnalyticalFieldMapping}
        />
      );
    }
    case 'pullAndMapWithValues': {
      return (
        <PullAndMapWithValuesSection
          integration={integration}
          onCreate={handleCreateAnalyticalFieldMapping}
          onUpdate={handleUpdateAnalyticalFieldMapping}
          onDelete={handleDeleteAnalyticalFieldMapping}
        />
      );
    }
    default:
      rejectUnexpectedValue(
        'customFieldsCapability',
        analyticalFieldsCapability,
      );
  }
};
